import FormateCurrency from 'utils/FormateCurrency';
import formateQTY from 'utils/FormateQty';
import FormateText from 'utils/FormateText';

export const PORTFOLIO_ACTIVITY_TYPES = [
    'ADD NEW COIN',
    'ADD MORE COIN',
    'EDIT COIN',
    'DELETE_COIN',
    'SELL_CRYPTO',
    'UPDATED_SELL',
];

export const ACTIVITY_FILTER_OPTIONS = [
    { key: 'ADD NEW COIN', label: 'Add New' },
    { key: 'ADD MORE COIN', label: 'Add More' },
    { key: 'EDIT COIN', label: 'Updated' },
    { key: 'DELETE_COIN', label: 'Deleted' },
    { key: 'SELL_CRYPTO', label: 'Sold' },
    { key: 'UPDATED_SELL', label: 'XGain Updates' },
    { key: 'LOGIN', label: 'Log Ins' },
    { key: 'EXPORT_EXCEL', label: 'Exports' },
];

export const ACTIVITY_BADGE_COLORS = {
    'ADD NEW COIN': { bg: 'rgba(36, 136, 34, 0.35)', color: '#95F205' },
    'ADD MORE COIN': { bg: 'rgba(149, 242, 5, 0.15)', color: '#95F205' },
    'EDIT COIN': { bg: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24' },
    'DELETE_COIN': { bg: 'rgba(187, 68, 68, 0.35)', color: '#f87171' },
    'SELL_CRYPTO': { bg: 'rgba(59, 130, 246, 0.25)', color: '#93c5fd' },
    'UPDATED_SELL': { bg: 'rgba(168, 85, 247, 0.2)', color: '#d8b4fe' },
    LOGIN: { bg: 'rgba(148, 163, 184, 0.2)', color: '#cbd5e1' },
    EXPORT_EXCEL: { bg: 'rgba(148, 163, 184, 0.2)', color: '#cbd5e1' },
    REGISTER: { bg: 'rgba(148, 163, 184, 0.2)', color: '#cbd5e1' },
};

const dataOf = (activity) => activity?.acitivity_data || {};

export const getActivityLocation = (activityOrData) => {
    const d = activityOrData?.acitivity_data ? activityOrData.acitivity_data : activityOrData;
    if (!d) return null;
    if (d.exchange) return d.exchange;
    if (d.wallet) {
        try {
            const parsed = typeof d.wallet === 'string' ? JSON.parse(d.wallet) : d.wallet;
            if (Array.isArray(parsed) && parsed.length) return parsed.join(', ');
        } catch {
            return null;
        }
    }
    return null;
};

export const getActivitySymbol = (activity) => {
    const symbol = dataOf(activity).symbol;
    if (!symbol) return null;
    return String(symbol).replaceAll('_', '').toUpperCase();
};

const fmtMoney = (value) => {
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return '—';
    return FormateCurrency(num);
};

const fmtQty = (value) => {
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return '—';
    return formateQTY(num);
};

const valuesEqual = (a, b) => {
    if (a === b) return true;
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
    return String(a ?? '') === String(b ?? '');
};

const joinParts = (parts) => parts.filter(Boolean).join(' · ');

export const getActivitySummary = (activity) => {
    try {
        if (!activity?.activity_type) return 'Unknown activity';
        const d = dataOf(activity);

        switch (activity.activity_type) {
        case 'ADD NEW COIN':
            return joinParts([
                `Added ${fmtQty(d.qty)}`,
                d.avg_buy != null ? `@ avg ${fmtMoney(d.avg_buy)}` : null,
                d.category ? d.category : null,
                getActivityLocation(d) ? `@ ${getActivityLocation(d)}` : null,
            ]);
        case 'ADD MORE COIN': {
            const oldQty = parseFloat(d.old_qty);
            const addedQty = parseFloat(d.qty);
            const newQty = Number.isFinite(oldQty) && Number.isFinite(addedQty)
                ? oldQty + addedQty
                : null;
            const oldAvg = d.old_avg_buy ?? d.avg_buy;
            const newAvg = d.new_avg_buy;
            return joinParts([
                `+${fmtQty(d.qty)}`,
                d.price != null ? `@ ${fmtMoney(d.price)}` : null,
                Number.isFinite(oldQty) && newQty != null ? `Qty ${fmtQty(oldQty)} → ${fmtQty(newQty)}` : null,
                oldAvg != null && newAvg != null ? `Avg ${fmtMoney(oldAvg)} → ${fmtMoney(newAvg)}` : null,
            ]);
        }
        case 'EDIT COIN': {
            const changes = [];
            if (!valuesEqual(d.old_qty, d.qty)) changes.push(`Qty ${fmtQty(d.old_qty)} → ${fmtQty(d.qty)}`);
            if (!valuesEqual(d.old_avg_buy, d.avg_buy)) changes.push(`Avg ${fmtMoney(d.old_avg_buy)} → ${fmtMoney(d.avg_buy)}`);
            if (!valuesEqual(d.old_category, d.category)) changes.push(`${d.old_category || '—'} → ${d.category || '—'}`);
            if (!valuesEqual(d.old_ATH, d.ATH)) changes.push(`ATH ${fmtMoney(d.old_ATH)} → ${fmtMoney(d.ATH)}`);
            if (!valuesEqual(d.old_ATL, d.ATL)) changes.push(`ATL ${fmtMoney(d.old_ATL)} → ${fmtMoney(d.ATL)}`);
            return changes.length ? changes.join(' · ') : 'Position updated';
        }
        case 'DELETE_COIN':
            return joinParts([
                'Removed position',
                `${fmtQty(d.qty)} @ avg ${fmtMoney(d.avg_buy)}`,
                d.category ? d.category : null,
            ]);
        case 'SELL_CRYPTO': {
            const prevQty = parseFloat(d.prevQty);
            const soldQty = parseFloat(d.qty);
            const afterQty = Number.isFinite(prevQty) && Number.isFinite(soldQty)
                ? prevQty - soldQty
                : null;
            return joinParts([
                `Sold ${fmtQty(d.qty)}`,
                d.selling_price != null ? `@ ${fmtMoney(d.selling_price)}` : null,
                afterQty != null ? `Qty ${fmtQty(prevQty)} → ${fmtQty(afterQty)}` : null,
            ]);
        }
        case 'UPDATED_SELL':
            return joinParts([
                d.xgain != null ? `${d.xgain}X` : null,
                d.grand_total_sell != null ? `pROI ${fmtMoney(d.grand_total_sell)}` : null,
                d.total_profit_sell != null ? `Profit ${fmtMoney(d.total_profit_sell)}` : null,
            ]);
        case 'LOGIN':
            return 'Signed in';
        case 'EXPORT_EXCEL':
            return 'Exported portfolio data';
        case 'REGISTER':
            return 'Account registered';
        default:
            return FormateText(activity.activity_type);
    }
    } catch (err) {
        console.error('Activity summary error:', err, activity);
        return 'Activity details unavailable';
    }
};

const field = (label, value) => ({
    label,
    value: value == null || value === '' ? '—' : value,
});

export const getActivityDetail = (activity) => {
    const d = dataOf(activity);
    const notes = activity.notes || d.notes || null;
    const symbol = getActivitySymbol(activity) || '—';

    switch (activity.activity_type) {
        case 'ADD NEW COIN':
            return {
                mode: 'inline',
                title: 'Initial position',
                fields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.qty)),
                    field('Avg Buy', fmtMoney(d.avg_buy)),
                    field('ATH', fmtMoney(d.ATH)),
                    field('ATL', fmtMoney(d.ATL)),
                    field('Category', d.category),
                    ...(getActivityLocation(d) ? [field('Location', getActivityLocation(d))] : []),
                    ...(notes ? [field('Notes', notes)] : []),
                ],
            };
        case 'ADD MORE COIN': {
            const oldAvg = d.old_avg_buy ?? d.avg_buy;
            return {
                mode: 'newOld',
                title: 'Add more',
                newFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.qty)),
                    field('Avg Buy', fmtMoney(d.new_avg_buy)),
                    field('Price', fmtMoney(d.price)),
                ],
                oldFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.old_qty)),
                    field('Avg Buy', fmtMoney(oldAvg)),
                    ...(notes ? [field('Notes', notes)] : []),
                ],
            };
        }
        case 'EDIT COIN':
            return {
                mode: 'newOld',
                title: 'Position changes',
                newFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.qty)),
                    field('Avg Buy', fmtMoney(d.avg_buy)),
                    field('ATH', fmtMoney(d.ATH)),
                    field('ATL', fmtMoney(d.ATL)),
                    field('Category', d.category),
                ],
                oldFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.old_qty)),
                    field('Avg Buy', fmtMoney(d.old_avg_buy)),
                    field('ATH', fmtMoney(d.old_ATH)),
                    field('ATL', fmtMoney(d.old_ATL)),
                    field('Category', d.old_category),
                    ...(notes ? [field('Notes', notes)] : []),
                ],
            };
        case 'DELETE_COIN':
            return {
                mode: 'inline',
                title: 'Removed position',
                fields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.qty)),
                    field('Avg Buy', fmtMoney(d.avg_buy)),
                    field('ATH', fmtMoney(d.ATH)),
                    field('ATL', fmtMoney(d.ATL)),
                    field('Category', d.category),
                    ...(notes ? [field('Notes', notes)] : []),
                ],
            };
        case 'SELL_CRYPTO': {
            const prevQty = parseFloat(d.prevQty);
            const soldQty = parseFloat(d.qty);
            const afterQty = Number.isFinite(prevQty) && Number.isFinite(soldQty)
                ? prevQty - soldQty
                : null;
            return {
                mode: 'newOld',
                title: 'Sell transaction',
                newFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.qty)),
                    field('Selling Price', fmtMoney(d.selling_price)),
                    ...(afterQty != null ? [field('Qty After', fmtQty(afterQty))] : []),
                ],
                oldFields: [
                    field('Symbol', symbol),
                    field('Qty', fmtQty(d.prevQty)),
                    ...(notes ? [field('Notes', notes)] : []),
                ],
            };
        }
        case 'UPDATED_SELL':
            return {
                mode: 'inline',
                title: 'XGain / sell plan',
                fields: [
                    field('Symbol', symbol),
                    field('XGain', d.xgain != null ? `${d.xgain}X` : '—'),
                    field('Grand Total Sell', fmtMoney(d.grand_total_sell)),
                    field('Total Profit', fmtMoney(d.total_profit_sell)),
                    field('Exit 1 price', fmtMoney(d.sell1_price)),
                    field('Exit 2 price', fmtMoney(d.sell2_price)),
                    field('Exit 3 price', fmtMoney(d.sell3_price)),
                    field('Exit 4 price', fmtMoney(d.sell4_price)),
                    field('Exit 1 %', d.sell1_set != null ? `${d.sell1_set}%` : '—'),
                    field('Exit 2 %', d.sell2_set != null ? `${d.sell2_set}%` : '—'),
                    field('Exit 3 %', d.sell3_set != null ? `${d.sell3_set}%` : '—'),
                    field('Exit 4 %', d.sell4_set != null ? `${d.sell4_set}%` : '—'),
                ].filter((item) => item.value !== '—'),
            };
        case 'LOGIN':
            return { mode: 'minimal', title: 'Session', fields: [] };
        case 'EXPORT_EXCEL':
            return { mode: 'minimal', title: 'Export', fields: [] };
        default:
            return { mode: 'minimal', title: 'Activity', fields: [] };
    }
};

export const formatActivityDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};
